// D7-FE1 — the two NON-QR states of MachineQRCodeDialog (plan
// `B7_D7_D8_ONE_CODE_DEFAULT_ON_PLAN.md` §3, owner ruling D7).
//
// Split out of MachineQRCodeDialog so both files stay well under 300 lines,
// mirroring the NameplateRowV2 / NameplateRowDetailV2 split.
//
// Why a dedicated state instead of a fallback QR: under D7 a machine carries
// exactly ONE code — the nameplate id-link. When the plate is off (or the fetch
// failed) there is genuinely nothing correct to encode, and silently falling
// back to the kiosk URL would print a sticker that opens a sign-in screen
// instead of the digital plate. A wrong-but-working QR is worse than no QR:
// it gets glued to a machine. Do NOT add a fallback payload here.
import React from "react";
import { Box, Button } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { dt } from "../utils/dt";

export interface MachineQRCodeDialogNoticeProps {
  variant: "plateOff" | "error";
  title?: string;
  body: string;
  /** Secondary line — e.g. what to do when the user cannot reach Settings. */
  hint?: string;
  actionLabel?: string;
  onAction?: () => void;
}

const PALETTE = {
  plateOff: { bg: "#FEF9E7", border: "#F9E79F", accent: "#F39C12", text: "#92400E" },
  error: { bg: "#F9EBEA", border: "#F5B7B1", accent: "#C0392B", text: "#922B21" },
};

const MachineQRCodeDialogNotice = ({
  variant,
  title,
  body,
  hint,
  actionLabel,
  onAction,
}: MachineQRCodeDialogNoticeProps) => {
  const palette = PALETTE[variant];
  const Icon = variant === "error" ? ErrorOutlineIcon : InfoOutlinedIcon;

  return (
    <Box
      sx={{
        mt: 3,
        p: 2,
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        backgroundColor: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: dt.radiusSm,
        animation: "fadeUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) both",
      }}
    >
      <Icon sx={{ fontSize: 20, color: palette.accent, flexShrink: 0, mt: "1px" }} />

      <Box sx={{ minWidth: 0, flex: 1 }}>
        {title && (
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13.5px",
              fontWeight: 700,
              color: palette.text,
              mb: 0.5,
            }}
          >
            {title}
          </Box>
        )}

        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            lineHeight: 1.55,
            color: palette.text,
          }}
        >
          {body}
        </Box>

        {hint && (
          <Box
            sx={{
              mt: 0.75,
              fontFamily: dt.font,
              fontSize: "12.5px",
              lineHeight: 1.5,
              color: dt.textSecondary,
            }}
          >
            {hint}
          </Box>
        )}

        {actionLabel && onAction && (
          <Button
            onClick={onAction}
            variant="outlined"
            size="small"
            sx={{
              mt: 1.5,
              fontFamily: dt.font,
              fontSize: "12.5px",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: dt.radiusSm,
              color: palette.text,
              borderColor: palette.border,
              backgroundColor: "#FFFFFF",
              "&:hover": {
                borderColor: palette.accent,
                backgroundColor: "#FFFFFF",
              },
            }}
          >
            {actionLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default MachineQRCodeDialogNotice;
